import { supabase } from '../lib/supabase.js';

const PUBLIC_API_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/public-veiculos`;
const SLUG_API_URL  = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/public-veiculo-slug`;

const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

/**
 * Fetches all vehicles from the public Edge Function.
 * No authentication required, but headers are included if ANON_KEY is present.
 * @returns {Promise<{data: Array, error: string|null}>}
 */
export async function fetchTodosVeiculos() {
  try {
    const headers = ANON_KEY
      ? {
          'apikey': ANON_KEY,
          'Authorization': `Bearer ${ANON_KEY}`,
          'Content-Type': 'application/json',
        }
      : {};
    const res = await fetch(`${PUBLIC_API_URL}?por_pagina=1000`, { headers });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json = await res.json();
    return { data: json.data ?? [], error: null };
  } catch (err) {
    console.error('[veiculos.service]', err);
    return { data: null, error: err.message };
  }
}

/**
 * Busca veículos com is_destaque = true (para a grid de populares).
 * @returns {Promise<{data: Array, error: string|null}>}
 */
export async function fetchVeiculosDestaque() {
  const { data, error } = await fetchTodosVeiculos();
  if (error || !data) return { data: null, error };
  
  const destaques = data.filter((v) => v.is_destaque === true);
  if (destaques.length > 0) {
    return { data: destaques, error: null };
  } else {
    // Fallback: retorna os 3 mais recentes/primeiros se não houver destaques
    return { data: data.slice(0, 3), error: null };
  }
}

/**
 * Busca veículos com is_destaque = false (para a grid de estoque).
 * @returns {Promise<{data: Array, error: string|null}>}
 */
export async function fetchVeiculosDisponiveis() {
  const { data, error } = await fetchTodosVeiculos();
  if (error || !data) return { data: null, error };
  return { data: data.filter((v) => v.is_destaque !== true), error: null };
}

/**
 * Busca um veículo público pelo slug via edge function `public-veiculo-slug`.
 * As fotos já vêm embutidas em `veiculo.fotos_veiculo`.
 * @param {string} slug
 * @returns {Promise<{data: object|null, error: string|null}>}
 */
export async function fetchVeiculoPorSlug(slug) {
  try {
    const headers = ANON_KEY
      ? {
          'apikey': ANON_KEY,
          'Authorization': `Bearer ${ANON_KEY}`,
          'Content-Type': 'application/json',
        }
      : {};

    const url = `${SLUG_API_URL}?slug=${encodeURIComponent(slug)}`;
    const res = await fetch(url, { headers });

    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const json = await res.json();

    if (!json.ok || !json.veiculo) {
      return { data: null, error: json.message || 'Veículo não encontrado' };
    }

    return { data: json.veiculo, error: null };
  } catch (err) {
    console.error('[veiculos.service] fetchVeiculoPorSlug', err);
    return { data: null, error: err.message };
  }
}

/**
 * @deprecated Use fetchVeiculoPorSlug para buscas por slug.
 * Mantido por compatibilidade com código legado.
 */
export async function fetchVeiculoPorIdOuSlug(identifier) {
  return fetchVeiculoPorSlug(String(identifier));
}
